import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "490",
  "slug": "what-is-an-enclave-territories-inside-other-countries",
  "title": "What Is an Enclave? The Strange Geography of Territories Inside Other Countries",
  "excerpt": "Some countries are entirely surrounded by another, and some pieces of land are cut off from their own nation. Here is the difference between enclaves and exclaves, with real examples.",
  "date": "2026-06-19",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1588860974533-1b811f36712b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxib3JkZXIlMjBsYW5kc2NhcGUlMjBjb3VudHJ5c2lkZXxlbnwxfDB8fHwxNzgxODU2NzkyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "An enclave is a territory completely surrounded by another single country, like Lesotho inside South Africa. An exclave is a part of a country that is geographically detached from its main territory, like Russia's Kaliningrad. Some places, such as Vatican City, are both at once.",
  "content": [
    {
      "type": "paragraph",
      "text": "Most borders separate one country neatly from the next. But the political map is full of stranger arrangements: nations marooned entirely inside their neighbour, towns that belong to a country they do not touch, and a few places so tangled that streets are split between two states. These oddities are the world of enclaves and exclaves, and the words are worth getting straight."
    },
    {
      "type": "heading",
      "text": "What Is an Enclave?"
    },
    {
      "type": "paragraph",
      "text": "An enclave is a territory that is entirely surrounded by the land of a single other country. To reach the rest of the world, you have to pass through the surrounding state. The cleanest examples are whole nations that happen to be enclaved. Lesotho is a sovereign country completely encircled by South Africa. San Marino and Vatican City are both surrounded entirely by Italy. Each is independent, yet you cannot leave it by land without entering its single neighbour."
    },
    {
      "type": "heading",
      "text": "What Is an Exclave?"
    },
    {
      "type": "paragraph",
      "text": "An exclave is a part of a country that is separated from the main body of that country and lies detached, usually surrounded by one or more other states. The classic example is Kaliningrad, a region of Russia that sits on the Baltic coast between Poland and Lithuania, with no land connection to the rest of Russia. It is an exclave of Russia — cut off from the motherland — but because it borders two countries and the sea rather than being wrapped in just one, it is not a true enclave."
    },
    {
      "type": "paragraph",
      "text": "That distinction is the heart of the matter. Enclave describes a territory from the point of view of being surrounded; exclave describes it from the point of view of being detached from its own country. The two ideas often overlap but are not the same."
    },
    {
      "type": "heading",
      "text": "When a Place Is Both"
    },
    {
      "type": "paragraph",
      "text": "Some territories qualify as both an enclave and an exclave at once: detached from their parent country and completely surrounded by a single other state. A few well-known cases:"
    },
    {
      "type": "list",
      "items": [
        "Llivia: a Spanish town separated from Spain and entirely surrounded by France.",
        "Busingen: a German village wholly enclosed by Switzerland.",
        "Campione d'Italia: an Italian town surrounded by Swiss territory on the shore of Lake Lugano.",
        "Vatican City and San Marino: independent states that are also enclaved within Italy."
      ]
    },
    {
      "type": "heading",
      "text": "The Most Tangled Borders"
    },
    {
      "type": "paragraph",
      "text": "A handful of places push the concept to its limit. The twin towns of Baarle-Hertog and Baarle-Nassau on the Belgium-Netherlands border are split into dozens of small Belgian and Dutch parcels, some of them counter-enclaves — bits of one country inside a bit of the other inside the first country again. The international boundary there runs straight through houses, gardens and cafes, marked by lines of paving on the ground. For decades the border between India and Bangladesh contained one of the most complex enclave clusters in the world, until the two nations exchanged the territories in 2015 to simplify daily life for the people living in them."
    },
    {
      "type": "heading",
      "text": "Why These Oddities Are Worth Knowing"
    },
    {
      "type": "paragraph",
      "text": "Enclaves and exclaves are reminders that borders are human creations, full of history, compromise and the occasional accident. They also make for some of the most surprising facts in geography. Knowing that a country can be wrapped entirely inside another, or stranded far from its own borders, sharpens your sense of how the political map really fits together — exactly the kind of knowledge that pays off when an unexpected flag or signpost appears in a round of EarthGuessr."
    }
  ]
};
